import {
    DataGrid,
    GridColumnsConfigProvider,
    Input,
    sharpDateFormatter
} from '@finbeat/react-toolkit';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import { groupBy } from 'lodash';
import { CalcCover, CalcInfo } from 'models/api/order';
import DataGridProp from 'models/dataGridProp';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useEffect, useMemo, useState } from 'react';
import { RowsChangeData } from 'react-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import { FaAngleDown, FaAngleRight } from 'react-icons/fa';
import { Card, CardHeader, FormGroup } from 'reactstrap';
import '../OrderInfo.scss';
import OrderCalcCoversTable from './OrderCalcsCoversTable';

type CalcMaster = {
    groupName: string;
    accrualAmount: number;
    coverageAmount: number;
    balance: number;
};

type MasterRow = CalcMaster & { type: 'MASTER'; expanded: boolean };

type DetailRow = CalcInfo & { parentGroupName?: string; type: 'DETAIL'; expanded: boolean };

type CoversRow = { parentGroupName?: string; covers: CalcCover[]; type: 'COVERS' };

type CalcInfoTable = DataGridProp & (MasterRow | DetailRow | CoversRow);

type SummaryRows = {
    accrualAmountSum: number;
    coverageAmountSum: number;
    balanceSum: number;
};

const OrderCalcsTable = ({ calcs, loading }: { calcs: CalcInfo[]; loading: boolean }) => {
    const className = 'order-calcs';
    const { t } = useTranslation();
    const { dateTimeFormat } = useGeneralSettingsContext();

    const [tableInfo, setTableInfo] = useState<CalcInfoTable[]>([]);
    const [groupedTableRows, setGroupedTableRows] = useState<Record<string, DetailRow[]>>();
    const [groupByValue, setGroupByValue] = useState('');

    const getGroupedRows = (groupByValue: string, tempCalcs: DetailRow[], dateTimeFormat: string): Record<string, DetailRow[]> | undefined => {
        switch (groupByValue) {
            case 'service':
                return groupBy(tempCalcs, (i) => i.service);
            case 'date':
                return groupBy(tempCalcs, (i) =>
                    `${sharpDateFormatter(i.fd, dateTimeFormat) ?? ''} - ${sharpDateFormatter(i.td, dateTimeFormat) ?? ''}`
                );
            default:
                return undefined;
        }
    };

    const updateTableState = (
        groupedRows: Record<string, DetailRow[]> | undefined,
        tempCalcs: DetailRow[],
        setGroupedTableRows: (rows: Record<string, DetailRow[]> | undefined) => void,
        setTableInfo: (info: CalcInfoTable[]) => void
    ) => {
        if (groupedRows) {
            setGroupedTableRows(groupedRows);
            setTableInfo(groupTableRows(groupedRows));
        } else {
            setGroupedTableRows(undefined);
            setTableInfo(tempCalcs);
        }
    };

    const groupTableRows = (grouped: Record<string, CalcInfo[]>) => {
        return Object.entries(grouped).reduce<CalcInfoTable[]>((res, [key, value]) => {
            res = [
                ...res,
                {
                    groupName: key,
                    accrualAmount: value.reduce((sum, x) => (sum += x.accrualAmount), 0),
                    coverageAmount: value.reduce((sum, x) => (sum += x.coverageAmount), 0),
                    balance: value.reduce((sum, x) => (sum += x.balance ?? 0), 0),
                    type: 'MASTER',
                    expanded: false,
                },
            ];
            return res;
        }, []);
    };

    useEffect(() => {
        const tempCalcs = calcs.map<DetailRow>((x) => ({ ...x, type: 'DETAIL', expanded: false }));
        const groupedRows = getGroupedRows(groupByValue, tempCalcs, dateTimeFormat);
        updateTableState(groupedRows, tempCalcs, setGroupedTableRows, setTableInfo);
    }, [groupByValue, calcs, dateTimeFormat]);

    const options = [
        {
            key: '',
            value: 'BackOfficeNoGrouping',
        },
        {
            key: 'service',
            value: 'BackOfficeService',
        },
        {
            key: 'date',
            value: 'BackOfficeDate',
        },
    ];

    const summaryValues = calcs.reduce(
        (sum, item) => {
            sum.accrualAmountSum += item.accrualAmount;
            sum.coverageAmountSum += item.coverageAmount;
            sum.balanceSum += item.balance ?? 0;
            return sum;
        },
        {
            accrualAmountSum: 0,
            coverageAmountSum: 0,
            balanceSum: 0,
        }
    );

    const summaryRows = [
        {
            accrualAmountSum: summaryValues.accrualAmountSum,
            coverageAmountSum: summaryValues.coverageAmountSum,
            balanceSum: summaryValues.balanceSum,
        },
    ];

    const onRowsChange = (
        rows: CalcInfoTable[],
        { indexes }: RowsChangeData<CalcInfoTable, SummaryRows>
    ) => {
        const row = rows[indexes[0]];

        if (row.type === 'MASTER' && groupedTableRows) {
            if (!row.expanded) {
                setTableInfo(
                    rows.filter((x) => x.type === 'MASTER' || x.parentGroupName !== row.groupName)
                );
            } else {
                const detailItems: CalcInfoTable[] = groupedTableRows[row.groupName].map((x) => ({
                    ...x,
                    parentGroupName: row.groupName,
                    type: 'DETAIL',
                }));

                rows.splice(indexes[0] + 1, 0, ...detailItems);
                setTableInfo(rows);
            }
        }

        if (row.type === 'DETAIL') {
            if (!row.expanded) {
                rows.splice(indexes[0] + 1, 1);
            } else {
                rows.splice(indexes[0] + 1, 0, {
                    parentGroupName: row.parentGroupName,
                    type: 'COVERS',
                    covers: row.covers,
                });
            }
            setTableInfo(rows);
        }
    };

    const blockSize = useMemo(
        () =>
            tableInfo
                ? tableInfo.reduce(
                    (sum, val) =>
                        sum + (val.type === 'COVERS' ? 85 + val.covers.length * 45 : 60),
                    70
                )
                : 90,
        [tableInfo]
    );

    if (calcs.length > 0) {
        return (
            <>
                <Input
                    type="select"
                    onChange={(e) => {
                        setGroupByValue(e.target.value);
                    }}
                    options={options.map((x) => (
                        <option key={x.key} value={x.key}>
                            {t(x.value)}
                        </option>
                    ))}
                    isFloating={false}
                />
                <FormGroup className={className}>
                    <Card className="card-for-table">
                        <CardHeader>{t('BackOfficeAccruals')}</CardHeader>
                        <GridColumnsConfigProvider<CalcInfoTable, SummaryRows>
                            storagePath="order-calcs-grid"
                            initColumns={[
                                {
                                    key: 'loanServiceName',
                                    draggable: false,
                                    name: <Trans i18nKey={'BackOfficeType'} />,
                                    colSpan: (args) => {
                                        if (args.type === 'SUMMARY') {
                                            return 4;
                                        }
                                        if (args.type === 'ROW' && args.row.type === 'MASTER') {
                                            return 4;
                                        }
                                        if (args.type === 'ROW' && args.row.type === 'COVERS') {
                                            return 8;
                                        }
                                    },
                                    formatter: ({ row, onRowChange }) => {
                                        if (row.type === 'COVERS') {
                                            return <OrderCalcCoversTable covers={row.covers} />;
                                        }

                                        if (row.type === 'DETAIL') {
                                            return <>{row.loanServiceName}</>;
                                        }

                                        return (
                                            <div
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    onRowChange({ ...row, expanded: !row.expanded });
                                                }}
                                                className="cursor--pointer"
                                            >
                                                {row.expanded ? <FaAngleDown /> : <FaAngleRight />}
                                                {row.groupName}
                                            </div>
                                        );
                                    },
                                    summaryFormatter: () => <>{t('BackOfficeTotal')}</>,
                                    summaryCellClass: 'order-info__table-summary',
                                    cellClass: (row) =>
                                        row.type === 'COVERS' ? `${className}__detail` : undefined,
                                },
                                {
                                    key: 'service',
                                    draggable: false,
                                    name: <Trans i18nKey={'BackOfficeService'} />,
                                },
                                {
                                    key: 'fd',
                                    draggable: false,
                                    name: <Trans i18nKey={'BackOfficeDtFrom'} />,
                                    width: 150,
                                    formatter: ({ row }) => (
                                        <>{row.type === 'DETAIL' && <DataTimeText value={row.fd} />}</>
                                    ),
                                },
                                {
                                    key: 'td',
                                    draggable: false,
                                    width: 150,
                                    name: <Trans i18nKey={'BackOfficeDtTo'} />,
                                    formatter: ({ row }) => (
                                        <>{row.type === 'DETAIL' && <DataTimeText value={row.td} />}</>
                                    ),
                                },
                                {
                                    key: 'accrualAmount',
                                    draggable: false,
                                    name: <Trans i18nKey={'BackOfficeAccrualAmount'} />,
                                    formatter: ({ row }) =>
                                        row.type === 'DETAIL' ? (
                                            <Money value={row.accrualAmount} />
                                        ) : (
                                            <></>
                                        ),
                                    summaryFormatter: ({ row }) => (
                                        <Money value={row.accrualAmountSum} />
                                    ),
                                    summaryCellClass: 'order-info__table-summary',
                                },
                                {
                                    key: 'coverageAmount',
                                    draggable: false,
                                    name: <Trans i18nKey={'BackOfficeCoverageAmount'} />,
                                    formatter: ({ row }) =>
                                        row.type === 'DETAIL' ? (
                                            <Money value={row.coverageAmount} />
                                        ) : (
                                            <></>
                                        ),
                                    summaryFormatter: ({ row }) => (
                                        <Money value={row.coverageAmountSum} />
                                    ),
                                    summaryCellClass: 'order-info__table-summary',
                                },
                                {
                                    key: 'balance',
                                    draggable: false,
                                    name: <Trans i18nKey={'BackOfficeBalanceOfDebt'} />,
                                    formatter: ({ row }) =>
                                        row.type === 'DETAIL' ? <Money value={row.balance} /> : <></>,
                                    summaryFormatter: ({ row }) => <Money value={row.balanceSum} />,
                                    summaryCellClass: 'order-info__table-summary',
                                },
                                {
                                    key: 'status',
                                    draggable: false,
                                    name: <Trans i18nKey={'BackOfficeStatus'} />,
                                    summaryCellClass: 'order-info__table-summary',
                                    formatter({ row, onRowChange }) {
                                        if (row.type === 'MASTER' || row.type === 'COVERS') {
                                            return <></>;
                                        }

                                        if (!row.covers.length) {
                                            return <>{row.status}</>;
                                        }

                                        return (
                                            <a
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    onRowChange({ ...row, expanded: !row.expanded });
                                                }}
                                                href="#"
                                                className="toggle-covers"
                                            >
                                                {row.status}
                                            </a>
                                        );
                                    },
                                },
                            ]}
                        >
                            <DataGrid<CalcInfoTable, SummaryRows>
                                reactDataGreedProps={{
                                    defaultColumnOptions: {
                                        sortable: false,
                                        resizable: true,
                                    },
                                    summaryRows: summaryRows,
                                    onRowsChange: onRowsChange,
                                    rowHeight: ({ type, row }) =>
                                        type === 'ROW' && row.type === 'COVERS'
                                            ? 85 + row.covers.length * 45
                                            : 45,
                                }}
                                rows={tableInfo}
                                blockSize={blockSize}
                                minBlockSize={90}
                                loading={loading}
                            />
                        </GridColumnsConfigProvider>
                    </Card>
                </FormGroup>
            </>
        );
    }

    return <></>;
};

export default OrderCalcsTable;
